<div class="accounting-container">
    <!-- Header -->
    <div class="page-header">
        <h1 class="page-title">Accounting</h1>
        <p class="page-subtitle">Manage invoices, payments, and financial reports</p>
    </div>

    <!-- Tab Navigation -->
    <div class="tab-navigation">
        <button class="tab-btn" [class.active]="activeTab === 'overview'" (click)="setActiveTab('overview')">
            <mat-icon>dashboard</mat-icon>
            <span>Overview</span>
        </button>
        <button class="tab-btn" [class.active]="activeTab === 'invoices'" (click)="setActiveTab('invoices')">
            <mat-icon>receipt_long</mat-icon>
            <span>Invoices</span>
        </button>
        <button class="tab-btn" [class.active]="activeTab === 'payments'" (click)="setActiveTab('payments')">
            <mat-icon>payments</mat-icon>
            <span>Payments</span>
        </button>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="loading-container">
        <div class="loading-spinner"></div>
        <span>Loading data...</span>
    </div>

    <!-- Overview Tab -->
    <div *ngIf="!isLoading && activeTab === 'overview'" class="tab-content">
        <!-- Summary Cards -->
        <div class="summary-cards">
            <div class="summary-card revenue">
                <div class="card-icon">
                    <mat-icon>trending_up</mat-icon>
                </div>
                <div class="card-content">
                    <span class="card-label">Total Revenue</span>
                    <span class="card-value">{{ formatCurrency(financialSummary.totalRevenue) }}</span>
                </div>
            </div>
            <div class="summary-card expenses">
                <div class="card-icon">
                    <mat-icon>trending_down</mat-icon>
                </div>
                <div class="card-content">
                    <span class="card-label">Total Expenses</span>
                    <span class="card-value">{{ formatCurrency(financialSummary.totalExpenses) }}</span>
                </div>
            </div>
            <div class="summary-card profit">
                <div class="card-icon">
                    <mat-icon>account_balance</mat-icon>
                </div>
                <div class="card-content">
                    <span class="card-label">Net Profit</span>
                    <span class="card-value">{{ formatCurrency(financialSummary.netProfit) }}</span>
                </div>
            </div>
            <!-- <div class="summary-card outstanding">
                <div class="card-icon">
                    <mat-icon>pending_actions</mat-icon>
                </div>
                <div class="card-content">
                    <span class="card-label">Outstanding Balance</span>
                    <span class="card-value">{{ formatCurrency(financialSummary.outstandingBalance) }}</span>
                </div>
            </div> -->
        </div>

        <!-- Charts Section -->
        <div class="charts-section">
            <!-- Revenue Chart -->
            <div class="chart-card">
                <div class="chart-header">
                    <h3>Monthly Revenue vs Expenses</h3>
                </div>
                <div class="chart-container">
                    <div class="bar-chart">
                        <div class="chart-bars">
                            <div *ngFor="let month of monthlyRevenue" class="bar-group">
                                <div class="bars">
                                    <div class="bar revenue-bar" [style.height.%]="getBarHeight(month.revenue)"
                                        [title]="'Revenue: ' + formatCurrency(month.revenue)"></div>
                                    <div class="bar expense-bar" [style.height.%]="getBarHeight(month.expenses)"
                                        [title]="'Expenses: ' + formatCurrency(month.expenses)"></div>
                                </div>
                                <span class="bar-label">{{ month.month }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="chart-legend">
                        <div class="legend-item">
                            <span class="legend-color revenue"></span>
                            <span>Revenue</span>
                        </div>
                        <div class="legend-item">
                            <span class="legend-color expense"></span>
                            <span>Expenses</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Invoice Status -->
            <div class="chart-card">
                <div class="chart-header">
                    <h3>Invoice Status</h3>
                </div>
                <div class="status-summary">
                    <div class="status-item">
                        <div class="status-icon paid">
                            <mat-icon>check_circle</mat-icon>
                        </div>
                        <div class="status-info">
                            <span class="status-count">{{ financialSummary.paidInvoices }}</span>
                            <span class="status-label">Paid</span>
                        </div>
                    </div>
                    <div class="status-item">
                        <div class="status-icon pending">
                            <mat-icon>schedule</mat-icon>
                        </div>
                        <div class="status-info">
                            <span class="status-count">{{ financialSummary.pendingInvoices }}</span>
                            <span class="status-label">Pending</span>
                        </div>
                    </div>
                    <div class="status-item">
                        <div class="status-icon overdue">
                            <mat-icon>warning</mat-icon>
                        </div>
                        <div class="status-info">
                            <span class="status-count">{{ financialSummary.overdueInvoices }}</span>
                            <span class="status-label">Overdue</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Recent Transactions -->
        <div class="recent-section">
            <div class="section-card">
                <div class="section-header">
                    <h3>Recent Invoices</h3>
                    <button class="view-all-btn" (click)="setActiveTab('invoices')">View All</button>
                </div>
                <div class="table-container">
                    <table class="data-table">
                        <thead>
                            <tr>
                                <th>Invoice #</th>
                                <th>Customer</th>
                                <th>Amount</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let invoice of invoices.slice(0, 5)">
                                <td>{{ invoice.invoiceNumber }}</td>
                                <td>{{ invoice.customerName }}</td>
                                <td>{{ formatCurrency(invoice.totalAmount) }}</td>
                                <td>
                                    <span class="status-badge" [ngClass]="getInvoiceStatusClass(invoice.status)">
                                        {{ invoice.status }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="section-card">
                <div class="section-header">
                    <h3>Recent Payments</h3>
                    <button class="view-all-btn" (click)="setActiveTab('payments')">View All</button>
                </div>
                <div class="table-container">
                    <table class="data-table">
                        <thead>
                            <tr>
                                <th>Invoice #</th>
                                <th>Customer</th>
                                <th>Amount</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let payment of payments.slice(0, 5)">
                                <td>{{ payment.invoiceNumber }}</td>
                                <td>{{ payment.customerName }}</td>
                                <td>{{ formatCurrency(payment.amount) }}</td>
                                <td>
                                    <span class="status-badge" [ngClass]="getPaymentStatusClass(payment.status)">
                                        {{ payment.status }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>

    <!-- Invoices Tab -->
    <div *ngIf="!isLoading && activeTab === 'invoices'" class="tab-content">
        <div class="content-card">
            <!-- Filters -->
            <div class="filter-section">
                <div class="filter-row">
                    <div class="search-box">
                        <mat-icon>search</mat-icon>
                        <input type="text" [(ngModel)]="searchText" placeholder="Search invoices...">
                    </div>
                    <mat-form-field appearance="outline" class="filter-field">
                        <mat-label>Status</mat-label>
                        <mat-select [(ngModel)]="selectedStatus">
                            <mat-option *ngFor="let status of invoiceStatusOptions" [value]="status">
                                {{ status }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="filter-field">
                        <mat-label>From Date</mat-label>
                        <input matInput [matDatepicker]="fromPicker" [(ngModel)]="fromDate">
                        <mat-datepicker-toggle matIconSuffix [for]="fromPicker"></mat-datepicker-toggle>
                        <mat-datepicker #fromPicker></mat-datepicker>
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="filter-field">
                        <mat-label>To Date</mat-label>
                        <input matInput [matDatepicker]="toPicker" [(ngModel)]="toDate">
                        <mat-datepicker-toggle matIconSuffix [for]="toPicker"></mat-datepicker-toggle>
                        <mat-datepicker #toPicker></mat-datepicker>
                    </mat-form-field>
                    <button class="clear-btn" (click)="clearFilters()">
                        <mat-icon>refresh</mat-icon>
                        Clear
                    </button>
                </div>
            </div>

            <!-- Invoice Table -->
            <div class="table-container">
                <table class="data-table full-table">
                    <thead>
                        <tr>
                            <th>Invoice #</th>
                            <th>Customer</th>
                            <th>Order ID</th>
                            <th>Issue Date</th>
                            <th>Due Date</th>
                            <th>Amount</th>
                            <th>Paid</th>
                            <th>Status</th>
                            <!-- <th>Actions</th> -->
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let invoice of filteredInvoices">
                            <td class="invoice-number">{{ invoice.invoiceNumber }}</td>
                            <td>{{ invoice.customerName }}</td>
                            <td>{{ invoice.orderId }}</td>
                            <td>{{ invoice.issueDate }}</td>
                            <td>{{ invoice.dueDate }}</td>
                            <td class="amount">{{ formatCurrency(invoice.totalAmount) }}</td>
                            <td class="amount">{{ formatCurrency(invoice.paidAmount) }}</td>
                            <td>
                                <span class="status-badge" [ngClass]="getInvoiceStatusClass(invoice.status)">
                                    {{ invoice.status }}
                                </span>
                            </td>
                            <!-- <td class="actions">
                                <button class="action-btn" (click)="viewInvoice(invoice)" title="View">
                                    <mat-icon>visibility</mat-icon>
                                </button>
                                <button class="action-btn" (click)="editInvoice(invoice)" title="Edit">
                                    <mat-icon>edit</mat-icon>
                                </button>
                            </td> -->
                        </tr>
                        <tr *ngIf="filteredInvoices.length === 0">
                            <td colspan="9" class="no-data">No invoices found</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <!-- Payments Tab -->
    <div *ngIf="!isLoading && activeTab === 'payments'" class="tab-content">
        <div class="content-card">
            <!-- Filters -->
            <div class="filter-section">
                <div class="filter-row">
                    <div class="search-box">
                        <mat-icon>search</mat-icon>
                        <input type="text" [(ngModel)]="searchText" placeholder="Search payments...">
                    </div>
                    <mat-form-field appearance="outline" class="filter-field">
                        <mat-label>Status</mat-label>
                        <mat-select [(ngModel)]="selectedStatus">
                            <mat-option *ngFor="let status of paymentStatusOptions" [value]="status">
                                {{ status }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="filter-field">
                        <mat-label>From Date</mat-label>
                        <input matInput [matDatepicker]="payFromPicker" [(ngModel)]="fromDate">
                        <mat-datepicker-toggle matIconSuffix [for]="payFromPicker"></mat-datepicker-toggle>
                        <mat-datepicker #payFromPicker></mat-datepicker>
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="filter-field">
                        <mat-label>To Date</mat-label>
                        <input matInput [matDatepicker]="payToPicker" [(ngModel)]="toDate">
                        <mat-datepicker-toggle matIconSuffix [for]="payToPicker"></mat-datepicker-toggle>
                        <mat-datepicker #payToPicker></mat-datepicker>
                    </mat-form-field>
                    <button class="clear-btn" (click)="clearFilters()">
                        <mat-icon>refresh</mat-icon>
                        Clear
                    </button>
                </div>
            </div>

            <!-- Payment Table -->
            <div class="table-container">
                <table class="data-table full-table">
                    <thead>
                        <tr>
                            <th>Payment ID</th>
                            <th>Invoice #</th>
                            <th>Customer</th>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Method</th>
                            <th>Reference</th>
                            <th>Status</th>
                            <!-- <th>Actions</th> -->
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let payment of filteredPayments">
                            <td>{{ payment.paymentId }}</td>
                            <td class="invoice-number">{{ payment.invoiceNumber }}</td>
                            <td>{{ payment.customerName }}</td>
                            <td>{{ payment.paymentDate }}</td>
                            <td class="amount">{{ formatCurrency(payment.amount) }}</td>
                            <td>{{ payment.paymentMethod }}</td>
                            <td>{{ payment.referenceNumber }}</td>
                            <td>
                                <span class="status-badge" [ngClass]="getPaymentStatusClass(payment.status)">
                                    {{ payment.status }}
                                </span>
                            </td>
                            <!-- <td class="actions">
                                <button class="action-btn" (click)="viewPayment(payment)" title="View">
                                    <mat-icon>visibility</mat-icon>
                                </button>
                            </td> -->
                        </tr>
                        <tr *ngIf="filteredPayments.length === 0">
                            <td colspan="9" class="no-data">No payments found</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</div>
